import React, { useEffect, useState } from "react";
import { Table, Row, Col, Pagination } from "react-bootstrap";
import {
  FiEdit,
  FiTrash2,
  FiSearch,
  FiPlus,
  FiHome,
  FiChevronRight,
} from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getClassteacher,
  deleteClassteacher,
} from "../../store/slices/classteacher";
import { toast } from "react-hot-toast";

import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

export default function ClassTeacherList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [itemsPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");

  const {
    classteacherlist = [],
    loading,
    error,
  } = useSelector((state) => state.classteacher || {});

  /* -------- Fetch -------- */

  useEffect(() => {
    dispatch(getClassteacher());
  }, [dispatch]);

  /* -------- Delete -------- */

  const handleDelete = (id) => {
    if (!id) return;


    if (!window.confirm("Delete this class teacher?")) return;

    dispatch(deleteClassteacher(id))
      .unwrap()
      .then(() => {
        toast.success("Teacher details deleted");
        dispatch(getClassteacher());
      })
      .catch((err) => {
        toast.error(err || "Delete failed");
      });


  };

  /* -------- Edit -------- */

  const handleEdit = (id) => {
    navigate(`/institute/edit-classteacher/${id}`);
  };

  /* -------- Reset Page on Search -------- */

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  /* -------- Filter -------- */

  const filteredData = classteacherlist.filter((item) =>
    item.teacherId?.name
      ?.toLowerCase()
      .includes(search.toLowerCase())
  );

  /* -------- Pagination -------- */

  const totalPages = Math.ceil(
    filteredData.length / itemsPerPage
  );

  const startIndex =
    (currentPage - 1) * itemsPerPage;

  const paginatedData = filteredData.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  return (
    <>
      {/* ================= HEADER ================= */}


      <div className="page-header">
        <div className="breadcrumb-section">

          <div className="breadcrumb-left">

            <div className="home-icon">
              <Link to="/institute/home">
                <FiHome
                  style={{
                    color: "white",
                    marginTop: "-4px",
                  }}
                />
              </Link>
            </div>

            <FiChevronRight className="separator" />

            <span className="active">
              Class Teacher
            </span>

          </div>

          <Link
            to="/institute/add-class-teacher"
            className="add-btn"
          >
            <FiPlus />
            Add Class Teacher
          </Link>

        </div>
      </div>


      {/* ================= CARD ================= */}

      <div className="staff-card">

        {/* ================= SEARCH ================= */}

        <Row className="align-items-center mb-1">

          <Col md={4}></Col>

          <Col md={8} className="staff-tools">

            <div className="staff-search">

              <FiSearch />

              <input
                placeholder="Search by teacher name"
                value={search}
                onChange={(e) =>
                  setSearch(e.target.value)
                }
              />

            </div>

          </Col>

        </Row>


        {/* ================= TABLE ================= */}

        <div className="table-responsive">

          <Table className="staff-table align-middle">

            <thead>
              <tr>

                <th className="text-center">#</th>

                <th className="text-center">
                  Standard
                </th>

                <th className="text-center">
                  Section
                </th>

                <th className="text-center">
                  Teacher
                </th>

                <th className="text-center">
                  Action
                </th>

              </tr>
            </thead>


            <tbody>

              {/* ================= SKELETON ================= */}

              {loading ? (

                Array.from({ length: 8 }).map(
                  (_, index) => (

                    <tr key={index}>

                      <td className="text-center">
                        <Skeleton width={30} />
                      </td>

                      <td className="text-center">
                        <Skeleton width={120} />
                      </td>

                      <td className="text-center">
                        <Skeleton width={100} />
                      </td>

                      <td className="text-center">
                        <Skeleton width={140} />
                      </td>

                      <td className="text-center">

                        <Skeleton
                          width={75}
                          height={30}
                          borderRadius={5}
                        />

                      </td>

                    </tr>
                  )
                )

              ) : paginatedData.length === 0 ? (

                <tr>
                  <td
                    colSpan="5"
                    className="text-center"
                  >
                    No class teachers found
                  </td>
                </tr>

              ) : (

                paginatedData.map((item, i) => (

                  <tr key={item._id}>

                    <td className="text-center">
                      {startIndex + i + 1}
                    </td>

                    <td className="text-center">
                      {item.standardId?.name || "-"}
                    </td>

                    <td className="text-center">
                      {item.sectionId?.name || "-"}
                    </td>

                    <td className="text-center">
                      {item.teacherId?.name || "-"}
                    </td>

                    <td className="text-center">
                      <button
                        className="edit-btn"
                        onClick={() =>
                          handleEdit(item._id)
                        }
                      >
                        <FiEdit size={16} />
                      </button>
                      <button
                        className="edit-btn ms-2"
                        onClick={() =>
                          handleDelete(item._id)
                        }
                      >
                        <FiTrash2 size={16} />
                      </button>



                    </td>

                  </tr>

                ))

              )}

            </tbody>

          </Table>

        </div>


        {/* ================= PAGINATION ================= */}

        {!loading && totalPages > 0 && (

          <div className="d-flex justify-content-end my-3">

            <Pagination className="mb-0">

              <Pagination.Prev
                disabled={currentPage === 1}
                onClick={() =>
                  setCurrentPage((p) => p - 1)
                }
              />

              {Array.from(
                { length: totalPages },
                (_, index) => {

                  const page = index + 1;

                  if (
                    page === 1 ||
                    page === totalPages ||
                    (
                      page >= currentPage - 1 &&
                      page <= currentPage + 1
                    )
                  ) {
                    return (
                      <Pagination.Item
                        key={page}
                        active={page === currentPage}
                        onClick={() =>
                          setCurrentPage(page)
                        }
                      >
                        {page}
                      </Pagination.Item>
                    );
                  }

                  if (page === currentPage - 2) {
                    return (
                      <Pagination.Ellipsis
                        key={`left-${page}`}
                        disabled
                      />
                    );
                  }

                  if (page === currentPage + 2) {
                    return (
                      <Pagination.Ellipsis
                        key={`right-${page}`}
                        disabled
                      />
                    );
                  }

                  return null;
                }
              )}

              <Pagination.Next
                disabled={
                  currentPage === totalPages ||
                  totalPages === 0
                }
                onClick={() =>
                  setCurrentPage((p) => p + 1)
                }
              />

            </Pagination>

          </div>

        )}

      </div>
    </>


  );
}
